import { Subscribable, Subscription } from '@benzinga/subscribable';
import { NewsAlertsRequest, NewsAlertsRequestEvent } from './request';
import { NewsAlertsStore, NewsAlertsStoreEvent } from './store';
import { SafeError, SafePromise } from '@benzinga/safe-await';
import { CategoriesAlert, AuthorAlert, UnsubscribeAllKeyResponse } from './entities';

import { AuthenticationManager, Session, Sound } from '@benzinga/session';
import { oneRequestAtATime } from '@benzinga/utils';
import { CategoryId } from '@benzinga/advanced-news-manager';
import { IncomingAuthorResult, IncomingCategoryUpdateResult } from './ingress';

export type NewsAlertsManagerEvent = NewsAlertsStoreEvent | NewsAlertsRequestEvent;

export class NewsAlertsManager extends Subscribable<NewsAlertsManagerEvent> {
  private store: NewsAlertsStore;
  private request: NewsAlertsRequest;
  private storeSubscription?: Subscription<NewsAlertsStore>;
  private requestSubscription?: Subscription<NewsAlertsRequest>;
  private authenticationManager: AuthenticationManager;
  private fetchAlerts = oneRequestAtATime(async () => {
    const alerts = await this.request.getCategoriesAlerts();
    if (alerts.err) {
      return alerts;
    } else {
      this.store.setCategoriesAlerts(alerts.ok);
      return alerts;
    }
  });

  constructor(session: Session) {
    super();
    this.store = new NewsAlertsStore();
    this.request = new NewsAlertsRequest(session);

    this.authenticationManager = session.getManager(AuthenticationManager);
    const authenticationSubscription = this.authenticationManager.subscribe(event => {
      switch (event.type) {
        case 'authentication:session_update':
          this.fetchAlerts();
          break;
      }
    });
    session.onPreSessionClose(() => authenticationSubscription.unsubscribe());
    this.fetchAlerts();
  }

  public static getName = () => 'benzinga-news-alerts';

  public getCategoriesAlerts = async (force = false): SafePromise<CategoriesAlert[]> => {
    if (force || this.store.shouldWeFetchAlerts() === true) {
      return await this.fetchAlerts();
    } else {
      return { ok: this.store.getCategoriesAlerts() as CategoriesAlert[] };
    }
  };
  public getStoredCategoriesAlerts = (): CategoriesAlert[] | undefined => this.store.getCategoriesAlerts();

  public setCategoriesAlert = async (
    categoryIds: CategoryId,
    settings: { sound?: Sound; realtimeEmail?: boolean },
  ): SafePromise<CategoriesAlert[] | IncomingCategoryUpdateResult> => {
    const alerts = await this.request.setCategoriesAlerts(categoryIds, settings.sound, settings.realtimeEmail);
    if (alerts.err) {
      return alerts;
    } else {
      const cat = await this.getCategoriesAlerts(true);
      if (cat.err) {
        return cat;
      } else {
        this.store.setCategoriesAlerts(cat.ok);
        return cat;
      }
    }
  };

  public getCategoryIdFromTerm = async (term_id: string) => {
    const alerts = await this.getCategoriesAlerts(true);
    const category = alerts?.ok?.find(alert => alert.tids?.includes(Number(term_id)));
    return category?.id ?? null;
  };

  public createAuthorSubscription = async (authorId: number, authorName: string): SafePromise<IncomingAuthorResult> => {
    return await this.request.createAuthorSubscription(authorId, authorName);
  };

  public getAuthorSubscriptions = async (): SafePromise<AuthorAlert[]> => {
    const alerts = await this.request.getAuthorSubscriptions();
    if (alerts.err) {
      return alerts;
    } else {
      return { ok: Object.values(alerts.ok.data) as AuthorAlert[] };
    }
  };

  public removeAuthorSubscription = async (alertId: number): SafePromise<IncomingAuthorResult> => {
    return await this.request.removeAuthorSubscription(alertId);
  };

  public getUnsubscribeAllKey = async (): SafePromise<UnsubscribeAllKeyResponse> => {
    return await this.request.getUnsubscribeAllKey();
  };

  public unsubscribeAllRealtimeAlerts = async (): SafePromise<void> => {
    const keyResponse = await this.request.getUnsubscribeAllKey();
    if (keyResponse.err) {
      return keyResponse;
    }
    const unsubKey = keyResponse.ok?.data?.unsub_all_key;
    if (!unsubKey) {
      return {
        err: new SafeError('Missing unsubscribe key', 'missing_unsubscribe_all_key'),
      };
    }
    return await this.request.unsubscribeAllRealtime(unsubKey);
  };

  protected onFirstSubscription(): void {
    this.storeSubscription = this.store.listen(event => this.dispatch(event));
    this.requestSubscription = this.request.listen(event => this.dispatch(event));
  }

  protected onZeroSubscriptions(): void {
    this.requestSubscription?.unsubscribe();
    this.storeSubscription?.unsubscribe();
  }
}
